import type { OrderStatus, UserRole } from '@jalupro/shared';
import { useT, type Dict, type TFunc } from './I18nProvider';

/**
 * Cross-cutting strings shared by many screens: sidebar nav, portal/role labels,
 * order-status labels, the shared feedback primitives, and sign-out. Screen-specific
 * copy lives in each screen's own `<Screen>.i18n.ts`.
 */
export const commonDict: Dict = {
  en: {
    // Brand / portal
    portal_suffix: 'Portal',
    go_home: 'Go to dashboard',
    open_menu: 'Open menu',
    role_agent: 'Agent',
    role_super_admin: 'Super Admin',
    role_accountant: 'Accountant',
    role_business_manager: 'Business Manager',
    role_sales_manager: 'Sales Manager',
    role_partner: 'Partner',
    role_assistant: 'Assistant',
    role_warehouse: 'Warehouse',

    // Role descriptions (Area 1 §4.1; shown when creating/editing a user)
    role_desc_agent:
      'Sells. Own clients, own orders, own order values and commission. Sees nothing of another agent, and no stock.',
    role_desc_accountant:
      'Runs the fiscal loop: invoices, payments, reconciliation, exports — in units and statuses. Financial figures are the Super Admin’s only.',
    role_desc_business_manager:
      'Runs the day: order progress, stuck orders, logistics coordination. Works in units and status — never amounts.',
    role_desc_sales_manager:
      'Runs the sales force: agents, client coverage, volume in units, commercial approvals, client reassignment. No revenue figures.',
    role_desc_partner:
      'Strategic read-only view of market and sales performance in units. No amounts, no editing.',
    role_desc_assistant:
      'Executes: creates and maintains client records, enters orders on an agent’s behalf, re-sends documents. No amounts, no deletion.',
    role_desc_warehouse:
      'Picks, packs and hands over: pick list, lots, packing, labels. Sees no price anywhere.',
    role_desc_super_admin:
      'Owns the business and the system: all figures, every setting, users and roles, tags, deletion and financial approvals.',

    // Sidebar nav
    nav_group_control: 'Control',
    nav_group_operations: 'Operations',
    nav_group_finance: 'Finance',
    nav_group_market: 'Clients & market',
    nav_group_admin: 'Administration',
    nav_group_my_work: 'My work',
    nav_group_reference: 'Reference',
    nav_group_account: 'My account',
    nav_profile: 'Profile',
    nav_home: 'Home',
    nav_my_clients: 'My clients',
    nav_dashboard: 'Dashboard',
    nav_new_order: 'New order',
    nav_my_orders: 'My orders',
    nav_orders: 'Orders',
    nav_clients: 'Clients',
    nav_products: 'Products',
    nav_payments: 'Payments',
    nav_agents: 'Agents',
    nav_reports: 'Reports',
    nav_users: 'Users',
    nav_settings: 'Settings',

    // Order statuses
    status_NEW: 'New',
    status_PROFORMA_SENT: 'Proforma sent',
    status_ON_HOLD_ADMIN_REVIEW: 'On hold — review',
    status_PAID: 'Paid',
    status_INVOICED: 'Invoiced',
    status_SHIPPED: 'Shipped',
    status_DELIVERED: 'Delivered',
    status_DELIVERY_PROBLEM: 'Delivery problem',

    // Shared feedback primitives
    loading: 'Loading',
    try_again: 'Try again',

    // Sign out
    sign_out: 'Sign out',

    // Language switcher
    language: 'Language',

    // Office / "Agente responsable" (shared by profile + order form)
    office_MADRID: 'Madrid',
    office_BARCELONA: 'Barcelona',
    office_VALENCIA: 'Valencia',
    office_HEADQUARTER: 'Headquarter',
  },
  es: {
    portal_suffix: 'Portal',
    go_home: 'Ir al panel',
    open_menu: 'Abrir menú',
    role_agent: 'Agente',
    role_super_admin: 'Super Admin',
    role_accountant: 'Contable',
    role_business_manager: 'Business Manager',
    role_sales_manager: 'Sales Manager',
    role_partner: 'Partner',
    role_assistant: 'Asistente',
    role_warehouse: 'Almacén',

    role_desc_agent:
      'Vende. Sus clientes, sus pedidos, el valor de sus pedidos y su comisión. No ve nada de otro agente, ni stock.',
    role_desc_accountant:
      'Lleva el ciclo fiscal: facturas, cobros, conciliación, exportaciones — en unidades y estados. Los importes solo los ve el Super Admin.',
    role_desc_business_manager:
      'Lleva el día a día: progreso de pedidos, pedidos atascados, coordinación logística. Trabaja en unidades y estados — nunca importes.',
    role_desc_sales_manager:
      'Dirige la fuerza de ventas: agentes, cobertura de clientes, volumen en unidades, aprobaciones comerciales, reasignación de clientes. Sin cifras de facturación.',
    role_desc_partner:
      'Vista estratégica de solo lectura del mercado y del rendimiento comercial en unidades. Sin importes, sin edición.',
    role_desc_assistant:
      'Ejecuta: crea y mantiene fichas de cliente, introduce pedidos en nombre de un agente, reenvía documentos. Sin importes, sin borrado.',
    role_desc_warehouse:
      'Prepara, embala y entrega: lista de picking, lotes, embalaje, etiquetas. No ve ningún precio.',
    role_desc_super_admin:
      'Dueño del negocio y del sistema: todas las cifras, todos los ajustes, usuarios y roles, etiquetas, borrados y aprobaciones financieras.',

    nav_group_control: 'Control',
    nav_group_operations: 'Operación',
    nav_group_finance: 'Finanzas',
    nav_group_market: 'Clientes y mercado',
    nav_group_admin: 'Administración',
    nav_group_my_work: 'Mi trabajo',
    nav_group_reference: 'Referencia',
    nav_group_account: 'Mi cuenta',
    nav_profile: 'Perfil',
    nav_home: 'Inicio',
    nav_my_clients: 'Mis clientes',
    nav_dashboard: 'Panel',
    nav_new_order: 'Nuevo pedido',
    nav_my_orders: 'Mis pedidos',
    nav_orders: 'Pedidos',
    nav_clients: 'Clientes',
    nav_products: 'Productos',
    nav_payments: 'Pagos',
    nav_agents: 'Agentes',
    nav_reports: 'Informes',
    nav_users: 'Usuarios',
    nav_settings: 'Ajustes',

    status_NEW: 'Nuevo',
    status_PROFORMA_SENT: 'Proforma enviada',
    status_ON_HOLD_ADMIN_REVIEW: 'En espera — revisión',
    status_PAID: 'Pagado',
    status_INVOICED: 'Facturado',
    status_SHIPPED: 'Enviado',
    status_DELIVERED: 'Entregado',
    status_DELIVERY_PROBLEM: 'Problema de entrega',

    loading: 'Cargando',
    try_again: 'Reintentar',

    sign_out: 'Cerrar sesión',

    language: 'Idioma',

    office_MADRID: 'Madrid',
    office_BARCELONA: 'Barcelona',
    office_VALENCIA: 'Valencia',
    office_HEADQUARTER: 'Sede central',
  },
  de: {
    portal_suffix: 'Portal',
    go_home: 'Zum Dashboard',
    open_menu: 'Menü öffnen',
    role_agent: 'Agent',
    role_super_admin: 'Super Admin',
    role_accountant: 'Buchhaltung',
    role_business_manager: 'Business Manager',
    role_sales_manager: 'Sales Manager',
    role_partner: 'Partner',
    role_assistant: 'Assistenz',
    role_warehouse: 'Lager',

    role_desc_agent:
      'Verkauft. Eigene Kunden, eigene Bestellungen, eigene Bestellwerte und Provision. Sieht nichts von anderen Agenten und keinen Bestand.',
    role_desc_accountant:
      'Führt den Fiskalkreislauf: Rechnungen, Zahlungen, Abstimmung, Exporte — in Stück und Status. Beträge sieht nur der Super Admin.',
    role_desc_business_manager:
      'Führt das Tagesgeschäft: Bestellfortschritt, hängende Bestellungen, Logistikkoordination. Arbeitet in Stück und Status — nie in Beträgen.',
    role_desc_sales_manager:
      'Führt den Vertrieb: Agenten, Kundenabdeckung, Volumen in Stück, kommerzielle Freigaben, Kundenzuordnung. Keine Umsatzzahlen.',
    role_desc_partner:
      'Strategische Nur-Lese-Sicht auf Markt und Vertriebsleistung in Stück. Keine Beträge, keine Bearbeitung.',
    role_desc_assistant:
      'Führt aus: legt Kundendaten an und pflegt sie, erfasst Bestellungen im Namen eines Agenten, versendet Dokumente erneut. Keine Beträge, kein Löschen.',
    role_desc_warehouse:
      'Kommissioniert, verpackt und übergibt: Pickliste, Chargen, Verpackung, Etiketten. Sieht nirgends einen Preis.',
    role_desc_super_admin:
      'Besitzt Geschäft und System: alle Zahlen, jede Einstellung, Benutzer und Rollen, Tags, Löschungen und finanzielle Freigaben.',

    nav_group_control: 'Steuerung',
    nav_group_operations: 'Betrieb',
    nav_group_finance: 'Finanzen',
    nav_group_market: 'Kunden & Markt',
    nav_group_admin: 'Verwaltung',
    nav_group_my_work: 'Meine Arbeit',
    nav_group_reference: 'Referenz',
    nav_group_account: 'Mein Konto',
    nav_profile: 'Profil',
    nav_home: 'Start',
    nav_my_clients: 'Meine Kunden',
    nav_dashboard: 'Dashboard',
    nav_new_order: 'Neue Bestellung',
    nav_my_orders: 'Meine Bestellungen',
    nav_orders: 'Bestellungen',
    nav_clients: 'Kunden',
    nav_products: 'Produkte',
    nav_payments: 'Zahlungen',
    nav_agents: 'Agenten',
    nav_reports: 'Berichte',
    nav_users: 'Benutzer',
    nav_settings: 'Einstellungen',

    status_NEW: 'Neu',
    status_PROFORMA_SENT: 'Proforma gesendet',
    status_ON_HOLD_ADMIN_REVIEW: 'Wartet — Prüfung',
    status_PAID: 'Bezahlt',
    status_INVOICED: 'Fakturiert',
    status_SHIPPED: 'Versendet',
    status_DELIVERED: 'Zugestellt',
    status_DELIVERY_PROBLEM: 'Lieferproblem',

    loading: 'Lädt',
    try_again: 'Erneut versuchen',

    sign_out: 'Abmelden',

    language: 'Sprache',

    office_MADRID: 'Madrid',
    office_BARCELONA: 'Barcelona',
    office_VALENCIA: 'Valencia',
    office_HEADQUARTER: 'Hauptsitz',
  },
};

/** Office values that back the "Agente responsable" select (mirror ResponsibleOffice). */
export const OFFICE_VALUES = ['MADRID', 'BARCELONA', 'VALENCIA', 'HEADQUARTER'] as const;

/** Translator scoped to the shared/common dictionary. */
export function useCommonT(): TFunc {
  return useT(commonDict);
}

/** A function that maps an OrderStatus to its localized label. */
export function useStatusLabel(): (status: OrderStatus) => string {
  const t = useCommonT();
  return (status: OrderStatus) => t(`status_${status}`);
}

/** Localized office label ("Agente responsable" value → display). */
export function useOfficeLabel(): (office: string | null | undefined) => string {
  const t = useCommonT();
  return (office: string | null | undefined) => (office ? t(`office_${office}`) : '');
}

/** Localized role label for the portal chrome. */
export function useRoleLabel(): (role: UserRole) => string {
  const t = useCommonT();
  const map: Record<UserRole, string> = {
    AGENT: 'role_agent',
    SUPER_ADMIN: 'role_super_admin',
    ACCOUNTANT: 'role_accountant',
    BUSINESS_MANAGER: 'role_business_manager',
    SALES_MANAGER: 'role_sales_manager',
    PARTNER: 'role_partner',
    ASSISTANT: 'role_assistant',
    WAREHOUSE: 'role_warehouse',
  };
  return (role: UserRole) => t(map[role] ?? 'role_agent');
}

/** Localized one-line description of what a role can do (user create/edit help text). */
export function useRoleDescription(): (role: UserRole) => string {
  const t = useCommonT();
  const map: Record<UserRole, string> = {
    AGENT: 'role_desc_agent',
    SUPER_ADMIN: 'role_desc_super_admin',
    ACCOUNTANT: 'role_desc_accountant',
    BUSINESS_MANAGER: 'role_desc_business_manager',
    SALES_MANAGER: 'role_desc_sales_manager',
    PARTNER: 'role_desc_partner',
    ASSISTANT: 'role_desc_assistant',
    WAREHOUSE: 'role_desc_warehouse',
  };
  return (role: UserRole) => t(map[role] ?? 'role_desc_agent');
}
